import React, { useEffect, useState, useRef } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { fetchAllCountiesGHGData, CityGHGData } from '../../services/statewideGHGService';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend);

export const CountiesGHGChart: React.FC = () => {
  const [countiesData, setCountiesData] = useState<CityGHGData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCounty, setSelectedCounty] = useState<string | null>(null);
  const chartRef = useRef<ChartJS<'line'>>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await fetchAllCountiesGHGData();
        setCountiesData(result);
      } catch (err) {
        console.error('Error loading counties GHG data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load counties GHG data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '500px',
        fontSize: '16px',
        color: '#6b7280'
      }}>
        Loading counties GHG inventory data...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '500px',
        fontSize: '16px',
        color: '#ef4444'
      }}>
        Error: {error}
      </div>
    );
  }

  if (!countiesData || countiesData.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '500px',
        fontSize: '16px',
        color: '#6b7280'
      }}>
        No counties GHG inventory data available
      </div>
    );
  }

  // Get all unique years across all counties
  const allYears = new Set<number>();
  countiesData.forEach(county => {
    county.years.forEach(year => allYears.add(year));
  });
  const sortedYears = Array.from(allYears).sort((a, b) => a - b);

  // Color palette for counties
  const getColorForIndex = (index: number): string => {
    const colors = [
      '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
      '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
      '#14b8a6', '#f43f5e', '#a855f7', '#0ea5e9', '#22c55e'
    ];
    return colors[index % colors.length];
  };

  // Normalize each county's data to 2005 = 100%
  const datasets = countiesData.map((county, index) => {
    // Find 2005 baseline value
    const baseline2005Index = county.years.indexOf(2005);
    const baseline2005 = baseline2005Index >= 0 ? county.emissions[baseline2005Index] : null;
    
    if (!baseline2005 || baseline2005 === 0) {
      // Skip counties without 2005 data or zero baseline
      return {
        label: county.location_name,
        data: sortedYears.map(() => null),
        borderColor: getColorForIndex(index),
        backgroundColor: getColorForIndex(index),
        fill: false,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 5
      };
    }

    // Create array matching sortedYears length, normalized to 2005 = 100%
    const data = sortedYears.map(year => {
      const yearIndex = county.years.indexOf(year);
      if (yearIndex >= 0) {
        const emissions = county.emissions[yearIndex];
        // Normalize: (year_value / 2005_value) * 100
        return (emissions / baseline2005) * 100;
      }
      return null;
    });

    return {
      label: county.location_name,
      data: data,
      borderColor: getColorForIndex(index),
      backgroundColor: getColorForIndex(index),
      fill: false,
      tension: 0.4,
      borderWidth: selectedCounty === county.location_name ? 3 : 2,
      pointRadius: 0,
      pointHoverRadius: 5,
      pointBackgroundColor: getColorForIndex(index),
      pointBorderColor: '#ffffff',
      pointBorderWidth: 2
    };
  });

  const chartData = {
    labels: sortedYears,
    datasets: datasets
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: 'Counties GHG Emissions',
        font: {
          size: 16,
          weight: 'bold' as const
        },
        padding: {
          top: 10,
          bottom: 20
        }
      },
      tooltip: {
        mode: 'nearest' as const,
        intersect: true,
        callbacks: {
          title: (items: any[]) => {
            const countyName = items[0].dataset.label;
            return `${countyName} - Year: ${items[0].label}`;
          },
          label: (context: any) => {
            const value = context.parsed.y;
            if (value === null) return undefined;
            
            // Show percentage (normalized to 2005 = 100%)
            const percentage = value.toFixed(1);
            return `${percentage}% of 2005 baseline`;
          },
          filter: (tooltipItem: any) => {
            // Only show tooltip for the closest/selected dataset
            return true;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Year',
          font: {
            size: 12,
            weight: 'normal' as const
          }
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      y: {
        title: {
          display: true,
          text: 'Percentage of 2005 Baseline (%)',
          font: {
            size: 12,
            weight: 'normal' as const
          }
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          callback: function(value: any) {
            return value.toFixed(0) + '%';
          }
        },
        beginAtZero: true,
        suggestedMin: 0
      }
    },
    interaction: {
      mode: 'index' as const,
      intersect: false
    },
    onHover: (event: any, activeElements: any[]) => {
      // Change cursor to pointer on hover
      if (event.native && event.native.target) {
        event.native.target.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
      }
    }
  };

  // Handle click to select/deselect county
  const handleChartClick = (event: any) => {
    if (!chartRef.current) return;
    
    const chart = chartRef.current;
    const points = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
    
    if (points.length) {
      const firstPoint = points[0];
      const datasetIndex = firstPoint.datasetIndex;
      const clickedCounty = countiesData[datasetIndex];
      
      // Toggle selection
      setSelectedCounty(selectedCounty === clickedCounty.location_name ? null : clickedCounty.location_name);
    }
  };

  return (
    <div style={{ 
      width: '100%', 
      height: '500px',
      position: 'relative'
    }}>
      <Line
        ref={chartRef}
        data={chartData}
        options={chartOptions}
        onClick={handleChartClick}
      />
    </div>
  );
};

